// useVoiceSession — captura de micrófono + WebSocket al voice-gateway (dictado).
//
// Audio: AudioContext a 16 kHz + AudioWorklet que convierte Float32→PCM16 y
// reporta el nivel (RMS) para animar el orbe. Los chunks binarios van por WS
// al gateway (:3991 en dev, /voz detrás de nginx en producción), que responde
// transcript_partial (provisional) y transcript_final (consolidado).
import { useCallback, useEffect, useRef, useState } from 'react';
import { tokenStore } from '@/lib/api/client';

export type VoiceEstado = 'idle' | 'conectando' | 'escuchando' | 'finalizando' | 'error';

// Junta ~250 ms de audio (4000 muestras) antes de enviar cada frame binario
const SAMPLES_POR_ENVIO = 4000;
// Micrófono preferido (persistido); OJO: validar que siga existiendo al usarlo
const MIC_KEY = 'voz_mic_id';
// Dispositivos de entrada VIRTUALES que graban la salida, no la voz
const ES_VIRTUAL = /mezcla|stereo mix|monitor|virtual|loopback|what u hear/i;

const WORKLET_JS = `
class Pcm16 extends AudioWorkletProcessor {
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch && ch.length) {
      const out = new Int16Array(ch.length);
      let sum = 0;
      for (let i = 0; i < ch.length; i++) {
        const s = Math.max(-1, Math.min(1, ch[i]));
        out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        sum += s * s;
      }
      this.port.postMessage({ pcm: out.buffer, rms: Math.sqrt(sum / ch.length) }, [out.buffer]);
    }
    return true;
  }
}
registerProcessor('pcm16', Pcm16);
`;

/** Remuestreo lineal a 16 kHz (fallback para navegadores que ignoran el
 *  sampleRate del AudioContext, p.ej. Firefox a 44.1/48 kHz). */
function remuestrear16k(pcm: Int16Array, tasa: number): Int16Array<ArrayBuffer> {
  const n = Math.floor(pcm.length * 16000 / tasa);
  const out = new Int16Array(n);
  const paso = (pcm.length - 1) / (n - 1 || 1);
  for (let i = 0; i < n; i++) {
    const x = i * paso;
    const j = Math.floor(x);
    const f = x - j;
    out[i] = pcm[j] + (pcm[Math.min(j + 1, pcm.length - 1)] - pcm[j]) * f;
  }
  return out;
}

function wsUrl(): string {
  const cfg = (import.meta.env.VITE_VOICE_WS_URL as string | undefined) || 'ws://localhost:3991';
  if (cfg.startsWith('/')) {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    return `${proto}://${window.location.host}${cfg}`;
  }
  return cfg;
}

export interface VoiceSessionApi {
  estado: VoiceEstado;
  parcial: string;
  nivel: number; // 0..1 para la animación del orbe
  error: string | null;
  microfono: string;      // etiqueta del dispositivo en uso
  sinSenal: boolean;      // true si lleva >3 s escuchando puro silencio
  mics: { id: string; label: string }[];  // entradas disponibles (tras el permiso)
  micId: string;          // deviceId preferido ('' = default del navegador)
  cambiarMic: (id: string) => void;       // persiste y corta el dictado en curso
  iniciar: (modulo?: string, extras?: Record<string, unknown>) => Promise<void>;
  detener: () => void;
}

/** Mensajes del gateway más allá de la transcripción (Fase 2: estructuración). */
export interface CandidatoRx {
  id: string; nombre: string; concentracion: string | null; presentacion: string | null; similitud: number;
}
export interface ItemRxDetectado {
  farmaco: string; comercial?: string; concentracion?: string; via?: string; forma?: string;
  cantidad?: string; indicaciones?: string; candidatos: CandidatoRx[];
}
export interface VoiceExtra {
  type: 'form_fill' | 'receta_fill' | 'aviso' | 'estructurando';
  salida_id?: string;
  campos?: Record<string, unknown>;
  items?: ItemRxDetectado[];
  notas?: string[];
  mensaje?: string;
}

/** onFinal: recibe cada segmento consolidado del dictado (para insertarlo en el campo activo).
 *  onExtra: mensajes de estructuración (form_fill/aviso) para el panel del orbe. */
export function useVoiceSession(onFinal: (texto: string) => void, onExtra?: (m: VoiceExtra) => void): VoiceSessionApi {
  const [estado, setEstado] = useState<VoiceEstado>('idle');
  const [parcial, setParcial] = useState('');
  const [nivel, setNivel] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [microfono, setMicrofono] = useState('');
  const [sinSenal, setSinSenal] = useState(false);
  const [mics, setMics] = useState<{ id: string; label: string }[]>([]);
  const [micId, setMicId] = useState<string>(() => localStorage.getItem(MIC_KEY) ?? '');
  const conSenalTsRef = useRef(0);   // última vez que hubo señal real

  const wsRef = useRef<WebSocket | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const nivelSuaveRef = useRef(0);
  const nivelTsRef = useRef(0);
  const onFinalRef = useRef(onFinal);
  onFinalRef.current = onFinal;
  const onExtraRef = useRef(onExtra);
  onExtraRef.current = onExtra;

  const limpiar = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    void ctxRef.current?.close().catch(() => undefined);
    ctxRef.current = null;
    if (wsRef.current && wsRef.current.readyState <= WebSocket.OPEN) wsRef.current.close();
    wsRef.current = null;
    setNivel(0);
    setParcial('');
  }, []);

  useEffect(() => limpiar, [limpiar]); // desmontaje

  const iniciar = useCallback(async (modulo = 'general', extras?: Record<string, unknown>) => {
    if (estado === 'escuchando' || estado === 'conectando') return;
    setError(null);
    setEstado('conectando');
    try {
      const token = tokenStore.getAccess();
      if (!token) throw new Error('Sesión no iniciada');

      // 1) micrófono (requiere HTTPS o localhost). Si hay uno elegido/guardado se
      //    usa ese; si el navegador entrega un dispositivo VIRTUAL (Mezcla estéreo,
      //    Stereo Mix, Monitor…) — que graba los parlantes, no la voz — se salta
      //    automáticamente al primer micrófono real.
      const audioBase = { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true };
      const preferido = localStorage.getItem(MIC_KEY);
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: preferido ? { ...audioBase, deviceId: { exact: preferido } } : audioBase,
        });
      } catch {
        // el mic guardado ya no existe (se desconectó): caer al default y olvidarlo
        localStorage.removeItem(MIC_KEY);
        stream = await navigator.mediaDevices.getUserMedia({ audio: audioBase });
      }
      let track = stream.getAudioTracks()[0];

      // catálogo de entradas (con permiso ya concedido, las etiquetas son visibles)
      const entradas = (await navigator.mediaDevices.enumerateDevices())
        .filter((d) => d.kind === 'audioinput' && d.deviceId && d.deviceId !== 'default' && d.deviceId !== 'communications')
        .map((d) => ({ id: d.deviceId, label: d.label || 'Micrófono' }));
      setMics(entradas);

      if (ES_VIRTUAL.test(track?.label ?? '')) {
        const real = entradas.find((d) => !ES_VIRTUAL.test(d.label));
        if (real) {
          stream.getTracks().forEach((t) => t.stop());
          stream = await navigator.mediaDevices.getUserMedia({
            audio: { ...audioBase, deviceId: { exact: real.id } },
          });
          track = stream.getAudioTracks()[0];
          localStorage.setItem(MIC_KEY, real.id);
          setMicId(real.id);
        }
      }
      streamRef.current = stream;
      setMicrofono(track?.label || 'micrófono');
      setSinSenal(false);
      conSenalTsRef.current = performance.now();

      // 2) AudioContext a la tasa NATIVA del dispositivo + worklet PCM16.
      //    NO forzar 16 kHz: en varios navegadores/plataformas un contexto con
      //    tasa distinta a la del micrófono entrega SILENCIO exacto (RMS 0).
      //    El remuestreo a 16 kHz se hace abajo, antes de enviar.
      const ctx = new AudioContext();
      if (ctx.state === 'suspended') await ctx.resume();
      const tasaReal = ctx.sampleRate;
      ctxRef.current = ctx;
      const url = URL.createObjectURL(new Blob([WORKLET_JS], { type: 'application/javascript' }));
      await ctx.audioWorklet.addModule(url);
      URL.revokeObjectURL(url);
      const src = ctx.createMediaStreamSource(stream);
      const nodo = new AudioWorkletNode(ctx, 'pcm16');
      src.connect(nodo);

      // 3) WebSocket al gateway
      const ws = new WebSocket(wsUrl());
      ws.binaryType = 'arraybuffer';
      wsRef.current = ws;

      let cola: Int16Array[] = [];
      let muestras = 0;
      nodo.port.onmessage = (e: MessageEvent<{ pcm: ArrayBuffer; rms: number }>) => {
        // suavizado + throttle a ~25 fps: React no necesita un render por cada 8 ms de audio
        nivelSuaveRef.current = nivelSuaveRef.current * 0.7 + e.data.rms * 0.3;
        const ahora = performance.now();
        // detector de micrófono mudo: >3 s de silencio digital → avisar en el orbe
        if (e.data.rms > 0.0015) {
          conSenalTsRef.current = ahora;
          setSinSenal((s) => (s ? false : s));
        } else if (ahora - conSenalTsRef.current > 3000) {
          setSinSenal((s) => (s ? s : true));
        }
        if (ahora - nivelTsRef.current > 40) {
          nivelTsRef.current = ahora;
          setNivel(nivelSuaveRef.current);
        }
        if (ws.readyState !== WebSocket.OPEN) return;
        const arr = new Int16Array(e.data.pcm);
        cola.push(arr);
        muestras += arr.length;
        if (muestras >= SAMPLES_POR_ENVIO * (tasaReal / 16000)) {
          let junto = new Int16Array(muestras);
          let off = 0;
          for (const c of cola) { junto.set(c, off); off += c.length; }
          if (tasaReal !== 16000) junto = remuestrear16k(junto, tasaReal);
          ws.send(junto.buffer);
          cola = [];
          muestras = 0;
        }
      };

      ws.onopen = () => ws.send(JSON.stringify({ type: 'auth', token }));
      ws.onmessage = (ev) => {
        const msg = JSON.parse(ev.data as string) as {
          type: string; texto?: string; mensaje?: string;
          salida_id?: string; campos?: Record<string, unknown>; notas?: string[]; items?: unknown[];
        };
        switch (msg.type) {
          case 'form_fill':
          case 'receta_fill':
          case 'aviso':
          case 'estructurando':
            onExtraRef.current?.(msg as VoiceExtra);
            break;
          case 'ready':
            ws.send(JSON.stringify({ type: 'start', modulo, ...(extras ?? {}) }));
            break;
          case 'listening':
            setEstado('escuchando');
            break;
          case 'transcript_partial':
            setParcial(msg.texto ?? '');
            break;
          case 'transcript_final':
            setParcial('');
            if (msg.texto) onFinalRef.current(msg.texto);
            break;
          case 'stopped':
            setEstado('idle');
            limpiar();
            break;
          case 'error':
            setError(msg.mensaje ?? 'Error del asistente de voz');
            setEstado('error');
            limpiar();
            break;
        }
      };
      ws.onerror = () => {
        setError('No se pudo conectar con el asistente de voz');
        setEstado('error');
        limpiar();
      };
      ws.onclose = () => {
        setEstado((e) => (e === 'escuchando' || e === 'conectando' || e === 'finalizando' ? 'idle' : e));
        setNivel(0);
      };
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo acceder al micrófono');
      setEstado('error');
      limpiar();
    }
  }, [estado, limpiar]);

  const detener = useCallback(() => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      setEstado('finalizando');
      // dejar de capturar, pero esperar el flush del STT (últimos finales)
      streamRef.current?.getTracks().forEach((t) => t.stop());
      ws.send(JSON.stringify({ type: 'stop' }));
    } else {
      setEstado('idle');
      limpiar();
    }
  }, [limpiar]);

  const cambiarMic = useCallback((id: string) => {
    if (id) localStorage.setItem(MIC_KEY, id);
    else localStorage.removeItem(MIC_KEY);
    setMicId(id);
    // el cambio aplica al próximo dictado: cortar el actual si estaba activo
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      wsRef.current.send(JSON.stringify({ type: 'stop' }));
      setEstado('finalizando');
    }
  }, []);

  return { estado, parcial, nivel, error, microfono, sinSenal, mics, micId, cambiarMic, iniciar, detener };
}
